import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControlLabel, Grid, Switch, TextField, makeStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as yup from "yup";
import { useFormCombos } from '../../../../../../hooks/useFormCombos';
import { actionShowSpinner } from '../../../../../../redux-flow/action/Wrapper/Spinner';
import { removeUserFromGroup } from '../../../../../../services/groups.service';
import { createUser, createExternalUser, getUserById, getUserByName, updateUser } from '../../../../../../services/users.service';
import AddButton from '../../../../../Common/AddButton';
import ToastMessage from '../../../../../Common/ToastMessage';
import ModalBase from '../../../../utils/ModalBase';
import AddUserToGroup from '../../../Modals/AddUserToGroup';
import Header from '../../../components/Header';
import { ActionButton, ActionButtonsContainer, CardTitleContainer, Container, FormCardContainer, FormContainer, GroupBox, InputsContainer, LinkItem, ListContainer, SaveButtonContainer, SectionTitle } from './styles';

interface IUserData {
  userId: string;
  userName: string;
  role: string;
  abbreviation: string;
  directoryName: string;
  directoryId: string;
};

interface IUserOption {
  id: string;
  name: string;
};

interface IUserGroup {
  id: string;
  name: string;
  committeePackageName: string;
  committeePackageId: string;
};

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
}));

const schema = yup.object().shape({
  userName: yup.string().required(),
  userId: yup.string().required(),
  directoryName: yup.string().nullable(),
  directoryId: yup.string().nullable(),
  role: yup.string().required(),
});

const ConfigureUsers = ({actionShowSpinner}: any) => {
  const location = useLocation<{id?: string}>();
  const { t } = useTranslation();
  const { register, handleSubmit, watch, setValue } = useForm<IUserData>({
    resolver: yupResolver(schema),
  });
  const classes = useStyles();  
  const {
    getDirectors,
    directorsList,
    sanitizeDirectorsForSelect,
  } = useFormCombos();

  const [toastMessage, setToastMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [isExternal, setIsExternal] = useState(false);
  const [externalName, setExternalName] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [memberName, setMemberName] = useState('');
  const [addUserToNewGroupModalOpen, setAddUserToNewGroupModalOpen] = useState(false);
  const [userOptions, setUserOptions] = useState<IUserOption[]>([]);
  const [userGroups, setUserGroups] = useState<IUserGroup[]>([]);
  const [userId, setUserId] = useState<string | undefined>(undefined);

  useEffect(() => {    
    getDirectors();
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    
    if(id) {
      setUserId(id);
    }
  }, [location]);

  const fetchUser = useCallback(async(id: string) => {
    actionShowSpinner(true);
    
    try{
      if(userId){
        const user = await getUserById(id);

        setValue("userId", user.id);
        setValue("userName", user.name);
        setMemberName(user.name);
        setValue("abbreviation", user.abbreviation);
        setValue("role", user.role);
        setValue("directoryName", user.directoryName);
        setValue("directoryId", user.directoryId);

        setUserGroups(user.groups);
      }
    }catch(error: any) {
      const [ errorMessage ] = error?.response?.data?.errors || [t("softwareManagement.modules.users.errorFetchUser")];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: 'error',
        autoHideDuration: 4000,
      });
    }

    actionShowSpinner(false);
  }, [userId]);

  useEffect(() => {
    if(userId){
      fetchUser(userId);
    }
  }, [fetchUser, userId]);

  useEffect(() => {
    async function load() {
      actionShowSpinner(true);
      try {
        if (memberName?.length >= 3) {
          const userList = await getUserByName(memberName);

          setUserOptions(userList);
        } else {
          setUserOptions([]);
        }
      } catch(error: any) {
      }
      
      actionShowSpinner(false);
    }

    load();
  }, [memberName]);

  const handleUpdateUser = useCallback(async(data: IUserData) => {
    try{
      const { id } = await updateUser(data.userId, {
        role: data.role,
        groupId: 'na',
        abbreviation: data.abbreviation,
      });

      setToastMessage({
        open: true,
        content: t(`softwareManagement.modules.users.userUpdatedSuccessfully`),
        type: "success",
        autoHideDuration: 4000,
      });
    }catch(error: any){
      const [errorMessage] = error.response.data.errors  || [t(`softwareManagement.modules.users.userUpdateError`)];

      setToastMessage({
        open: true,
        content: errorMessage ||  t(`softwareManagement.modules.users.userUpdateError`),
        type: "error",
        autoHideDuration: 4000,
      });
    }
  }, []);

  const handleCreateUser = useCallback(async(data: IUserData) => {
    try{
      const { id } = await createUser({
        id: data.userId,
        role: data.role,
        directoryId: data.directoryId,
        groupId: 'na',
        abbreviation: data.abbreviation,
      });

      setUserId(id);

      setToastMessage({
        open: true,
        content: t(`softwareManagement.modules.users.userCreatedSuccessfully`),
        type: "success",
        autoHideDuration: 4000,
      });
    }catch(error: any){
      const [errorMessage] = error.response.data.errors || [t(`softwareManagement.modules.users.userCreationError`)];
      
      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }
  }, []);

  const handleCreateExternalUser = useCallback(async() => {
    try {
      const { id } = await createExternalUser({
        name: externalName,
        email: externalEmail,
        role: watch('role'),
      });

      setUserId(id);

      setToastMessage({
        open: true,
        content: t(`softwareManagement.modules.users.userCreatedSuccessfully`),
        type: "success",
        autoHideDuration: 4000,
      });
    } catch(error: any) {
      const [errorMessage] = error?.response?.data?.errors || [t(`softwareManagement.modules.users.userCreationError`)];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }
  }, [externalName, externalEmail, watch]);

  const onSubmit = useCallback((data: IUserData) => {
    if (isExternal) {
      handleCreateExternalUser();
      return;
    }

    if(userId){
      handleUpdateUser(data);
    }else{
      handleCreateUser(data);
    }
  }, [updateUser, handleCreateUser, handleCreateExternalUser, userId, isExternal]);

  const handleRemoveGroup = useCallback(async(groupId: string) => {
    if(!userId) return;

    try{
      await removeUserFromGroup({userId, groupId});

      fetchUser(userId);
    } catch(error: any){
      const [errorMessage] = error.response.data.errors || [t(`softwareManagement.modules.users.userRemoveGroupError`)];

      setToastMessage({
        open: true,
        content: errorMessage,
        type: "error",
        autoHideDuration: 4000,
      });
    }
  }, [userId, removeUserFromGroup, fetchUser]);
  
  return (
    <Container>
      <Header title={t(`softwareManagement.modules.users.userConfiguration`)}></Header>

      <FormContainer>
        <FormCardContainer>
          <SectionTitle>{t(`softwareManagement.modules.users.userInformation`)}</SectionTitle>

          <form onSubmit={isExternal ? (e) => { e.preventDefault(); handleCreateExternalUser(); } : handleSubmit(onSubmit)} onError={() => console.log('test')}>
            {!userId && (
              <Grid container style={{ marginBottom: 16, paddingLeft: 8 }}>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isExternal}
                        onChange={(e) => setIsExternal(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t("softwareManagement.modules.users.externalUser", "Membro Externo")}
                  />
                </Grid>
              </Grid>
            )}

            <InputsContainer container spacing={2}>
              {isExternal && !userId ? (
                <>
                  <Grid item lg={6} xs={12}>
                    <TextField
                      autoComplete='nope'
                      id="externalName"
                      label={t("grid.committee.columns.name")}
                      fullWidth
                      required
                      value={externalName}
                      onChange={(e) => setExternalName(e.target.value)}
                      InputLabelProps={{ shrink: externalName.length > 0 }}
                    />
                  </Grid>

                  <Grid item lg={6} xs={12}>
                    <TextField
                      autoComplete='nope'
                      id="externalEmail"
                      label={t("menu.managementProfiles.email")}
                      fullWidth
                      required
                      type="email"
                      value={externalEmail}
                      onChange={(e) => setExternalEmail(e.target.value)}
                      InputLabelProps={{ shrink: externalEmail.length > 0 }}
                    />
                  </Grid>

                  <Grid item lg={6} xs={12}>
                    <TextField
                      autoComplete='nope'
                      id="role"
                      label={t("softwareManagement.modules.users.role")}
                      fullWidth
                      {...register("role")}
                      InputLabelProps={{ shrink: watch('role') ? true : false }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item lg={6} xs={12}>
                    <Autocomplete
                      options={userOptions}
                      getOptionLabel={(option) => option.name || ""}
                      disabled={!!userId}
                      onChange={(event, newValue) => {
                        setValue('userId', newValue?.id || '');
                      }}
                      value={watch('userId') ? userOptions.find((user) => user.id === watch('userId')) : null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t(`softwareManagement.modules.users.title`)}
                          disabled={!!userId}
                          required
                          error={!watch('userName')}
                          value={watch('userName')}
                          {...register("userName")}
                          onChange={(event) => {
                            setMemberName(event.target.value);
                            setValue('userName', event.target.value);
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item lg={6} xs={12}>
                    <TextField
                      autoComplete='nope'
                      id="abbreviation"
                      label={t("softwareManagement.modules.users.abbreviation")}
                      fullWidth
                      {...register("abbreviation")}
                      InputLabelProps={{ shrink: watch('abbreviation') ? true : false }}
                    />
                  </Grid>

                  <Grid item lg={6} xs={12}>
                    <TextField
                      autoComplete='nope'
                      id="role"
                      label={t("softwareManagement.modules.users.role")}
                      fullWidth
                      required={true}
                      error={!watch('role')}
                      {...register("role")}
                      InputLabelProps={{ shrink: watch('role') ? true : false }}
                    />
                  </Grid>

                  <Grid item lg={6} xs={12}>
                    <Autocomplete
                      options={sanitizeDirectorsForSelect(directorsList)}
                      getOptionLabel={(option) => option?.name || ""}
                      disabled={!!userId}
                      onChange={(event, newValue) => {
                        setValue('directoryId', newValue?.id || '');
                      }}
                      value={watch('directoryId') ? sanitizeDirectorsForSelect(directorsList).find((user: {id: string}) => user.id === watch('directoryId')) : null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t(`softwareManagement.modules.users.directory`)}
                          value={watch('directoryName')}
                          {...register("directoryName")}
                          onChange={(event) => {
                            setValue('directoryName', event.target.value);
                          }}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </InputsContainer>

            <SaveButtonContainer>
              <Button
                variant="contained"
                className={classes.button}
                type="submit"
              >
                {t(`softwareManagement.modules.users.save`)}
              </Button>
            </SaveButtonContainer>
          </form>
        </FormCardContainer>

        {!isExternal && (
          <FormCardContainer>
            <CardTitleContainer>
              <SectionTitle>{t(`softwareManagement.modules.users.userGroups`)}</SectionTitle>

              <AddButton
                onClick={() => setAddUserToNewGroupModalOpen(true)}
                disabled={!userId}
              />
            </CardTitleContainer>

            <ListContainer>
              {userGroups.map((group) => (
                <GroupBox key={group.id}>
                  <LinkItem to={`/system-management/configure-groups?id=${group.id}`}>
                    {group.name}
                  </LinkItem>

                  <LinkItem to={`/system-management/configure-committee-packages?id=${group.committeePackageId}`}>
                    {group.committeePackageName}
                  </LinkItem>

                  <ActionButtonsContainer>
                    <ActionButton onClick={() => handleRemoveGroup(group.id)}>
                      <Close />
                    </ActionButton>
                  </ActionButtonsContainer>
                </GroupBox>
              ))}
            </ListContainer>
          </FormCardContainer>
        )}
      </FormContainer>

      <ModalBase 
        open={addUserToNewGroupModalOpen} 
        setOpen={setAddUserToNewGroupModalOpen}
        content={
          <AddUserToGroup
            setModalStatus={(value) => setAddUserToNewGroupModalOpen(value)}
            userId={userId || ''}
            handleAddToNewGroup={async(id: string) => fetchUser(id)}
            setToastMessage={setToastMessage}
          />
        }
      />
      
      <ToastMessage
        message={toastMessage.content}
        type={toastMessage.type || 'error'}
        open={toastMessage.open}
        close={() => setToastMessage({ ...toastMessage, open: false })}
      />
    </Container>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

const ConfigureUsersConnection = connect(
  null,
  mapDispatchToProps
)(ConfigureUsers);

export default ConfigureUsersConnection;